import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MoreOptionsIcon from './icons/MoreOptionsIcon';
import { MessageCircleIcon } from '@/components/profile/ProfileIcons';
import FavouriteIcon from './icons/FavouriteIcon';
import MarkFinishedIcon from './icons/MarkFinishedIcon';
import MarkUnfinishedIcon from './icons/MarkUnfinishedIcon';
import ResetProgressIcon from './icons/ResetProgressIcon';
import HideBookIcon from './icons/HideBookIcon';
import UnhideBookIcon from './icons/UnhideBookIcon';

interface MenuItemProps {
  label: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  icon: React.ReactNode;
  isDarkMode: boolean;
}

const MenuItem: React.FC<MenuItemProps> = React.memo(({ label, onClick, icon, isDarkMode }) => (
  <button
    onClick={onClick}
    className={`w-full cursor-pointer rounded-md px-3 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
      isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-200/50 text-gray-700'
    }`}
  >
    {icon}
    <span>{label}</span>
  </button>
));

interface MenuPanelProps {
  isDarkMode: boolean;
  onFavourite: (e: React.MouseEvent) => void;
  onMarkFinished: (e: React.MouseEvent) => void;
  onMarkUnfinished: (e: React.MouseEvent) => void;
  onShowConversations: (e: React.MouseEvent) => void;
  onToggleHidden: (e: React.MouseEvent) => void;
  isFav: boolean;
  isFinished: boolean;
  isUnstarted: boolean;
  isHidden: boolean;
}

const MenuPanel: React.FC<MenuPanelProps> = React.memo(({
  isDarkMode,
  onFavourite,
  onMarkFinished,
  onMarkUnfinished,
  onShowConversations,
  onToggleHidden,
  isFav,
  isFinished,
  isUnstarted,
  isHidden,
}) => {
  const iconColor = isDarkMode ? 'text-gray-200' : 'text-gray-500';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92, y: 0 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.92, y: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      style={{ transformOrigin: 'top center' }}
      className={`absolute right-0 top-full w-48 rounded-lg p-1.5 shadow-xl z-10 border ${
        isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'
      }`}
    >
      <MenuItem
        label="Conversations"
        onClick={onShowConversations}
        icon={<MessageCircleIcon size="xs" className={iconColor} />}
        isDarkMode={isDarkMode}
      />

      <MenuItem
        label={isFav ? "Unfavourite" : "Favourite"}
        onClick={onFavourite}
        icon={<FavouriteIcon size="xs" isFavourite={isFav} colorOutlined={iconColor} colorFilled={iconColor}/>}
        isDarkMode={isDarkMode}
      />

      <MenuItem
        label={isHidden ? "Unhide book" : "Hide book"}
        onClick={onToggleHidden}
        icon={isHidden ? <UnhideBookIcon size="xs" className={iconColor} /> : <HideBookIcon size="xs" className={iconColor} />}
        isDarkMode={isDarkMode}
      />
      {!isFinished && (
        <>
          <MenuItem
            label="Mark finished"
            onClick={onMarkFinished}
            icon={<MarkFinishedIcon size="xs" className={iconColor} />}
            isDarkMode={isDarkMode}
          />
        </>
      )}

      {!isUnstarted && (
        <>
          <MenuItem
            label={isFinished ? "Mark unfinished" : "Reset progress"}
            onClick={onMarkUnfinished}
            icon={isFinished ? (
              <MarkUnfinishedIcon size="xs" className={iconColor} />
            ) : (
              <ResetProgressIcon size="xs" className={iconColor} />
            )}
            isDarkMode={isDarkMode}
          />
        </>
      )}
    </motion.div>
  );
});

interface BookActionsMenuProps extends MenuPanelProps {
  isOpen: boolean;
  onToggleOpen: (e: React.MouseEvent) => void;
}

export const BookActionsMenu: React.FC<BookActionsMenuProps> = React.memo(({
  isDarkMode,
  isOpen,
  onToggleOpen,
  onFavourite,
  onMarkFinished,
  onMarkUnfinished,
  onShowConversations,
  onToggleHidden,
  isFav,
  isFinished,
  isUnstarted,
  isHidden,
}) => {
  return (
    <div className="relative book-menu-container">
      <button
        onClick={onToggleOpen}
        className={`h-6 w-6 flex items-center justify-center rounded-full transition-colors ${
          isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <MoreOptionsIcon size="md" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <MenuPanel
            isDarkMode={isDarkMode}
            onFavourite={onFavourite}
            onMarkFinished={onMarkFinished}
            onMarkUnfinished={onMarkUnfinished}
            onShowConversations={onShowConversations}
            onToggleHidden={onToggleHidden}
            isFav={isFav}
            isFinished={isFinished}
            isUnstarted={isUnstarted}
            isHidden={isHidden}
          />
        )}
      </AnimatePresence>
    </div>
  );
});

export default BookActionsMenu;
